import dayjs from "dayjs";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";

export const AirportRideContext = React.createContext({
  activeTab: 1,
  setActiveTab: () => {},
  firstStepValues: {
    agentUser: "",
    rideType: "",
    arrivalCity: "",
    airportName: "",
    terminalNumber: "",
    location: "",
    destination: "",
    locationCoords: null,
    destinationCoords: null,
    zone: "",
  },
  setFirstStepValues: () => {},
  secondStepValues: {
    vehicleType: "",
    sharedRide: false,
    seatNumber: 1,
    arrivalDate: dayjs().add(3, "hours"),
    arrivalTime: dayjs().add(3, "hours"),
    discount: 0,
    vehicleTypeIndex: 0,
  },
  setSecondStepValues: () => {},
  errorMessage: "",
  setErrorMessage: () => {},
});

/**
 * A provider that is used to wrap the data of the airport ride.
 * It stores the following values:
 *  - firstStepValues: The values submitted in the first form (The ride details step)
 *  - secondStepValues: The values submitted in the second form (The vehicle details step)
 *  - activeTab: A state that is used as an UI indicator for the user to show them which step they are in.
 * It also returns the following actions:
 *  - setFirstStepValues: A function to be called when the user finishes the first step. It saves the values from the form in the corresponding state.
 *  - setSecondStepValues: A function to be called when the user finishes the second step. It saves the values from the form in the corresponding state.
 *  - setActiveTab: A function to set the state of the active tab. Typically useful when the user is trying to go back into previous steps.
 *  - unsetEmail: A function that unsets the email to return the user to the first step.
 *
 * Moreover, it uses the state of the browser history to detect whether the user is trying to resubmit a failed payment.
 * It gets the previous data from the history state, then it fills the initial values of the form with the data from the failed payment.
 *
 *
 * @param {Object} props
 * @param {JSX.Element | JSX.Element[]} props.children - The elements inside the context provider
 * @returns {JSX.Element}
 */

export const AirportProvider = ({ children }) => {
  const { state } = useLocation();
  const [activeTab, setActiveTab] = useState(state ? 3 : 1);
  const [errorMessage, setErrorMessage] = useState("");
  const [firstStepValues, setFirstStepValues] = useState({
    agentUser: state?.user || "",
    rideType: state?.rideType || "",
    arrivalCity: state?.city || "",
    airportName: state?.airport || "",
    terminalNumber: state?.terminal || "",
    location: state?.location || "",
    destination: state?.destination_name || "",
    locationCoords:
      typeof state?.pick_up === "string"
        ? {
            lat: Number(state.pick_up.split(",")[0]),
            lng: Number(state.pick_up.split(",")[1]),
          }
        : null,
    destinationCoords:
      typeof state?.drop_off === "string"
        ? {
            lat: Number(state.drop_off.split(",")[0]),
            lng: Number(state.drop_off.split(",")[1]),
          }
        : null,
    zone: state?.zone || "",
  });
  const [secondStepValues, setSecondStepValues] = useState({
    vehicleType: state?.vehicle_type || "",
    sharedRide: !!state?.rider,
    seatNumber: state?.rider || 1,
    arrivalDate:
      state?.arrival_date && dayjs(state.arrival_date).isValid()
        ? dayjs(state.arrival_date)
        : null,
    arrivalTime: state?.arrival_time
      ? dayjs()
          .set("hours", state.arrival_time.split(":")[0])
          .set("minutes", state.arrival_time.split(":")[1])
      : null,
    discount: state?.ride_discount,
    vehicleTypeIndex: 0,
  });
  return (
    <AirportRideContext.Provider
      value={{
        activeTab,
        setActiveTab,
        errorMessage,
        setErrorMessage,
        firstStepValues,
        setFirstStepValues,
        secondStepValues,
        setSecondStepValues,
      }}
    >
      {children}
    </AirportRideContext.Provider>
  );
};
